import ExcelJS from 'exceljs';
import i18n from '../../../i18n';
import { BreakdownInfo } from '../../../pages/Home/types';
import { compareExportNamesDigitLetterPinyin } from '../../../utils/elementSortUtils';
import { buildExportDownloadFileName } from '../exportFileName';
import { SlotInfo, SlotType } from '../../../types/schedule.types';
import { writeWorkbookBufferWithProgress } from '../excelWriteProgress';
import {
  SlotDataCache,
  ScheduleDataCache,
  ElementDataCache,
  ElementRelationDataCache
} from '../../../types/cache.types';
import { ElementType } from '../../../types/element.types';

export type SupportedLanguage = 'zh-CN' | 'zh-HK' | 'en';

const t = (key: string, opts?: Record<string, unknown> & { lng?: string }) => i18n.t(key, opts);

const COLORS = {
  GRAY3: '#757575',
  BLACK: '#000000',
  BORDER: '#000000'
};

export const EXCEL_ELEMENT_QTY_MARGINS_INCH = {
  left: 0.35,
  right: 0.35,
  top: 0.35,
  bottom: 0.5,
  header: 0.3,
  footer: 0.3
} as const;

const hexToArgb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

const setCellBorder = (cell: ExcelJS.Cell) => {
  const borderColor = { argb: hexToArgb(COLORS.BORDER) };
  cell.border = {
    top: { style: 'thin', color: borderColor },
    left: { style: 'thin', color: borderColor },
    bottom: { style: 'thin', color: borderColor },
    right: { style: 'thin', color: borderColor }
  };
};

function applyPrintLayout(worksheet: ExcelJS.Worksheet, language: SupportedLanguage) {
  worksheet.pageSetup = {
    ...worksheet.pageSetup,
    orientation: 'landscape',
    horizontalCentered: true,
    margins: { ...EXCEL_ELEMENT_QTY_MARGINS_INCH },
    printTitlesRow: '1:2'
  };
  const footerBody = t('export.excelPageFooter', { lng: language });
  worksheet.headerFooter = {
    ...worksheet.headerFooter,
    oddFooter: `&C&8${footerBody}`
  };
}

const normalizeDate = (raw?: string): string | null => {
  if (!raw) return null;
  const v = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
};

function getShootingSortedDates(slots: SlotInfo[]): string[] {
  const shootSlots = slots.filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType);
  const dates = shootSlots
    .map((pt) => normalizeDate(pt.slotDate || ''))
    .filter((d): d is string => Boolean(d));
  return Array.from(new Set(dates)).sort();
}

function buildShootDayByDate(slots: SlotInfo[]): Map<string, number> {
  const map = new Map<string, number>();
  getShootingSortedDates(slots).forEach((dateStr, index) => {
    map.set(dateStr, index + 1);
  });
  return map;
}

function resolveRange(
  explicitRange: { start: string; end: string } | undefined,
  slotCache: SlotDataCache | null
): { start: string; end: string } | null {
  if (explicitRange?.start && explicitRange?.end && explicitRange.start <= explicitRange.end) {
    return explicitRange;
  }
  const dates = (slotCache?.slots || [])
    .filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType)
    .map((pt) => normalizeDate(pt.slotDate || ''))
    .filter((d): d is string => Boolean(d))
    .sort();
  if (dates.length === 0) return null;
  return { start: dates[0], end: dates[dates.length - 1] };
}

type QtyRow = {
  date: string;
  shootDay: number;
  typeOrder: number;
  typeLabel: string;
  name: string;
  qty: number;
};

export const exportElementQTYExcel = async (
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage = 'zh-CN',
  _secondaryLanguage?: SupportedLanguage,
  breakdownId?: string,
  effectiveScheduleId?: string | null,
  scheduleDetailCalDateBounds?: { start: string; end: string },
  getScheduleCache?: (breakdownId: string) => ScheduleDataCache | null,
  getSlotCache?: (scheduleId: string) => SlotDataCache | null,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null,
  onProgress?: (progress: number) => void
): Promise<void> => {
  onProgress?.(10);
  if (!breakdownId || !effectiveScheduleId || !getSlotCache || !getElementCache || !getElementRelationCache) {
    throw new Error(t('export.exportNeedScheduleId', { lng: language }) as string);
  }

  const slotCache = getSlotCache(effectiveScheduleId);
  const slots = slotCache?.slots || [];
  const elements = getElementCache(breakdownId)?.elements || [];
  const elementRelations = getElementRelationCache(breakdownId)?.elementRelations || [];
  const range = resolveRange(scheduleDetailCalDateBounds, slotCache || null);
  if (!range) throw new Error(t('export.exportInvalidDateRange', { lng: language }) as string);
  onProgress?.(30);

  const featuredLabel = t('schedule.featuredShort', { lng: language }) as string;
  const backgroundLabel = t('schedule.crowdShort', { lng: language }) as string;
  const itemMeta = new Map<string, { name: string; type: ElementType; typeOrder: number; typeLabel: string }>();
  elements.forEach((item) => {
    const typ = item.elementType;
    if (typ !== ElementType.FEATURED && typ !== ElementType.BACKGROUND) return;
    const name = (item.elementName || item.elementAbbr || '').trim();
    if (!name) return;
    itemMeta.set(item.elementID, {
      name,
      type: typ,
      typeOrder: typ === ElementType.FEATURED ? 0 : 1,
      typeLabel: typ === ElementType.FEATURED ? featuredLabel : backgroundLabel
    });
  });

  const relationsBySceneId = new Map<string, Array<{ itemId: string; qty: number }>>();
  elementRelations.forEach((rel) => {
    const meta = itemMeta.get(rel.elementID);
    if (!meta) return;
    const list = relationsBySceneId.get(rel.sceneID) || [];
    list.push({ itemId: rel.elementID, qty: Math.max(1, rel.elementQtyInScene || 1) });
    relationsBySceneId.set(rel.sceneID, list);
  });

  const shootDayByDate = buildShootDayByDate(slots);

  const daySceneIds = new Map<string, Set<string>>();
  slots.forEach((pt) => {
    const date = normalizeDate(pt.slotDate || '');
    if (!date) return;
    if (date < range.start || date > range.end) return;
    if (!(pt.slotType === SlotType.SHOOT || !pt.slotType)) return;
    if (!pt.slotBelongSceneID) return;
    const set = daySceneIds.get(date) || new Set<string>();
    set.add(pt.slotBelongSceneID);
    daySceneIds.set(date, set);
  });

  const rows: QtyRow[] = [];
  Array.from(daySceneIds.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([date, sceneIds]) => {
      const maxQtyByItem = new Map<string, number>();
      sceneIds.forEach((sceneId) => {
        const rels = relationsBySceneId.get(sceneId) || [];
        rels.forEach((r) => {
          const prev = maxQtyByItem.get(r.itemId) || 0;
          if (r.qty > prev) maxQtyByItem.set(r.itemId, r.qty);
        });
      });
      const shootDay = shootDayByDate.get(date);
      if (shootDay == null) return;
      maxQtyByItem.forEach((qty, itemId) => {
        const meta = itemMeta.get(itemId);
        if (!meta) return;
        rows.push({
          date,
          shootDay,
          typeOrder: meta.typeOrder,
          typeLabel: meta.typeLabel,
          name: meta.name,
          qty
        });
      });
    });

  rows.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.typeOrder !== b.typeOrder) return a.typeOrder - b.typeOrder;
    return compareExportNamesDigitLetterPinyin(a.name, b.name);
  });

  const workbook = new ExcelJS.Workbook();
  const sheetNameRaw = (t('export.actorsQTYTable', { lng: language }) as string) || 'Actors QTY';
  const worksheet = workbook.addWorksheet(sheetNameRaw.slice(0, 31));
  worksheet.columns = [{ width: 20 }, { width: 20 }, { width: 34 }, { width: 24 }, { width: 22 }];

  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') || '' : '';
  const scheduleHeaderInfo = slotCache?.scheduleInfo
    || getScheduleCache?.(breakdownId)?.schedules?.find((p) => p.scheduleID === effectiveScheduleId)
    || null;
  const scheduleVersionLabel = t('export.castPdfScheduleVersionLabel', { lng: language }) as string;
  const breakdownVersionLabel = t('export.castPdfBreakdownVersionLabel', { lng: language }) as string;
  const scriptVersionLabel = t('export.scriptVersion', { lng: language }) as string;
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  const title = `《${filmName}》${scheduleVersionLabel}${labelSeparator}${scheduleHeaderInfo?.scheduleName || ''}${pipeSeparator}${breakdownVersionLabel}${labelSeparator}${breakdownInfo.breakdownName || ''}${pipeSeparator}${scriptVersionLabel}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}`;
  worksheet.addRow([title]);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };

  const header = worksheet.addRow([
    t('export.shootDate', { lng: language }),
    t('export.shootDay', { lng: language }),
    t('export.elementNameLabel', { lng: language }),
    t('export.elementMaxQtyPerDay', { lng: language }),
    t('export.elementTypeLabel', { lng: language })
  ]);
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(COLORS.GRAY3) } };
    cell.font = { color: { argb: hexToArgb(COLORS.BLACK) }, size: 8, bold: false };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    setCellBorder(cell);
  });

  rows.forEach((r) => {
    const row = worksheet.addRow([r.date, r.shootDay, r.name, r.qty, r.typeLabel]);
    row.eachCell((cell) => {
      cell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
      setCellBorder(cell);
    });
  });

  applyPrintLayout(worksheet, language);
  onProgress?.(80);

  const fileName = buildExportDownloadFileName({
    lng: language,
    filmName,
    optionDisplayName: t('export.actorsQTYTable', { lng: language }) as string,
    optionFallbackI18nKey: 'export.actorsQTYTable',
    breakdownDisplayName: scheduleHeaderInfo?.scheduleName,
    breakdownFallbackI18nKey: 'export.schedule',
    extension: 'xlsx'
  });

  const buffer = await writeWorkbookBufferWithProgress(workbook, onProgress, { start: 80, end: 98 });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  onProgress?.(100);
};
